import type { Ref } from 'vue'
import type { Order as OrderType } from '@/services/orderService'
import type { OrderPerson } from '@/composables/useMultipleDiners'

type PaymentMethod = 'cash' | 'card' | 'digital' | 'other'

type NewOrderForm = {
  type: 'Dine-in' | 'Takeaway' | 'Delivery'
  tableId: number | string | null
  customerName: string
  notes: string
  items: Array<{
    menu_item_id: number
    variant_id?: number | null
    quantity: number
    notes?: string
    special_instructions?: string
    unit_price?: number
    extras?: Array<{ name: string; price: number; quantity: number }>
  }>
}

interface UseOrderEditHydrationOptions {
  form: Ref<NewOrderForm>
  persons: Ref<OrderPerson[]>
  activePersonIndex: Ref<number>
  isEditLoading: Ref<boolean>
  ivaEnabled: Ref<boolean>
  markAsPaid: Ref<boolean>
  selectedPaymentMethod: Ref<PaymentMethod>
  cashReceived: Ref<number>
  mapOrderTypeToFrontend: (backendType: string) => NewOrderForm['type']
  loadCustomerDataFromOrder: (order: OrderType) => void
  showError: (message: string) => void
}

export function useOrderEditHydration(options: UseOrderEditHydrationOptions) {
  const loadOrderIntoForm = async (order: OrderType) => {
    options.isEditLoading.value = true

    try {
      const backendOrderType = order.order_type || 'dine_in'
      options.form.value.type = options.mapOrderTypeToFrontend(backendOrderType)

      options.form.value.tableId = order.table_id ?? null
      options.form.value.customerName = order.customer_name || ''
      options.form.value.notes = order.notes || ''

      options.loadCustomerDataFromOrder(order)
      options.ivaEnabled.value = order.iva_enabled || false

      options.markAsPaid.value = order.is_paid || false

      const orderWithPayment = order as OrderType & {
        payment_method?: PaymentMethod
        amount_paid?: number
      }

      if (orderWithPayment.payment_method) {
        options.selectedPaymentMethod.value = orderWithPayment.payment_method
      }

      if (orderWithPayment.amount_paid) {
        options.cashReceived.value = orderWithPayment.amount_paid
      }

      const itemsByPerson = new Map<number | null, Array<OrderPerson['items'][number] & { status?: string }>>()

      ;(order.items || []).forEach((item) => {
        const personId = item.person_id ?? null

        if (!itemsByPerson.has(personId)) {
          itemsByPerson.set(personId, [])
        }

        itemsByPerson.get(personId)?.push({
          menu_item_id: item.menu_item_id,
          variant_id: item.variant_id ?? null,
          quantity: item.quantity,
          notes: item.special_instructions || undefined,
          special_instructions: item.special_instructions || undefined,
          unit_price: item.unit_price || item.menu_item?.price || 0,
          status: item.status || 'pending',
          person_id: personId
        })
      })

      if (itemsByPerson.size > 0) {
        options.persons.value = []

        const sortedPersonIds = Array.from(itemsByPerson.keys()).sort((a, b) => {
          if (a === null) return -1
          if (b === null) return 1
          return a - b
        })

        let position = 1
        sortedPersonIds.forEach((personId) => {
          const items = itemsByPerson.get(personId) || []
          options.persons.value.push({
            id: personId ?? undefined,
            position,
            name: '',
            items
          })
          position++
        })

        if (options.persons.value.length === 0) {
          options.persons.value.push({
            position: 1,
            name: '',
            items: []
          })
        }

        options.activePersonIndex.value = 0
      }
    } catch (error) {
      console.error('Error loading order into form:', error)
      options.showError('Error al cargar la orden para edición')
    } finally {
      options.isEditLoading.value = false
    }
  }

  return {
    loadOrderIntoForm
  }
}
